import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useAdminAPI } from '../../hooks/Admin/useAdminAPI'
import AdminManagement from './index'
import { GenericForm } from './GenericForm'
import { Building2 } from 'lucide-react'

import { buildSuggestedUsername } from './usernameHelpers'

const InstitutionAdminsManagement = () => {
  const [institutions, setInstitutions] = useState([])
  const { fetchEntities } = useAdminAPI()

  useEffect(() => {
    let isMounted = true
    const loadInstitutions = async () => {
      try {
        const result = await fetchEntities('instituciones')
        if (isMounted) {
          const items = Array.isArray(result) ? result : result.data || []
          setInstitutions(items)
        }
      } catch (error) {
        console.error('Failed to load institutions', error)
      }
    }
    loadInstitutions()
    return () => { isMounted = false }
  }, [fetchEntities])

  const columns = useMemo(() => [
    { key: 'name', label: 'Nombre', render: (item) => `${item.nombre} ${item.apellido}` },
    { key: 'email', label: 'Email' },
    { key: 'dni', label: 'DNI' },
    { key: 'username', label: 'Usuario' },
    {
      key: 'institucion',
      label: 'Institución',
      render: (item) => {
        const alcance = item.UsuarioAlcanceInstitucion || item.usuarioAlcanceInstitucion || item.usuario_alcance_institucion
        const instId = alcance?.idInstitucion
        const instName = institutions.find(i => i.idInstitucion === instId)?.nombre || ' - '
        return instName
      }
    }
  ], [institutions])

  const FormComponent = (props) => {
    const { initialData } = props
    const [usernameEditedManually, setUsernameEditedManually] = useState(Boolean(initialData?.username))

    const flattenedData = useMemo(() => {
      if (!initialData) return null
      const alcance = initialData.UsuarioAlcanceInstitucion || initialData.usuarioAlcanceInstitucion || initialData.usuario_alcance_institucion
      return {
        ...initialData,
        idInstitucion: alcance?.idInstitucion || initialData.idInstitucion
      }
    }, [initialData])

    useEffect(() => {
      setUsernameEditedManually(Boolean(initialData?.username))
    }, [initialData])

    const handleFieldChange = useCallback((name, value, updatedData) => {
      if (name === 'username') {
        setUsernameEditedManually((value || '').trim() !== '')
        return updatedData
      }

      if (initialData || usernameEditedManually) return updatedData
      if (name !== 'nombre' && name !== 'apellido') return updatedData

      const suggestedUsername = buildSuggestedUsername(updatedData.nombre, updatedData.apellido)
      if (!suggestedUsername) return updatedData

      return {
        ...updatedData,
        username: suggestedUsername
      }
    }, [initialData, usernameEditedManually])

    const formFields = useMemo(() => [
      { key: 'dni', label: 'DNI', type: 'number', required: true },
      {
        key: 'genero',
        label: 'Género',
        required: true,
        type: 'select',
        options: [
          { value: 'Masculino', label: 'Masculino' },
          { value: 'Femenino', label: 'Femenino' }
        ]
      },
      { key: 'nombre', label: 'Nombre', required: true },
      { key: 'apellido', label: 'Apellido', required: true },
      { key: 'username', label: 'Usuario', required: true },
      { key: 'email', label: 'Correo electrónico', type: 'email', required: true },
      { key: 'clave', label: 'Contraseña', type: 'password', required: !initialData },
      {
        key: 'confirmarClave',
        label: 'Repetir contraseña',
        type: 'password',
        required: !initialData,
        matchesField: 'clave',
        errorMessage: 'Las contraseñas no coinciden.',
        clientOnly: true
      },
      {
        key: 'idInstitucion',
        label: 'Institución',
        type: 'select',
        required: true,
        options: institutions.map(i => ({
          value: i.idInstitucion,
          label: `${i.nombre} - ${i.calle} ${i.numero}`
        }))
      }
    ].filter(f => !initialData || !['clave', 'confirmarClave'].includes(f.key)), [initialData, institutions])

    return (
      <GenericForm
        {...props}
        initialData={flattenedData}
        fields={formFields}
        title='Administrador de Institución'
        createTitle='Nuevo Admin. de Institución'
        onFieldChange={handleFieldChange}
      />
    )
  }

  return (
    <AdminManagement
      entityType='Administradores de Institución'
      headerSubtitle='Desde acá podés administrar usuarios con rol de administrador institucional, asignándoles su institución obligatoria correspondiente.'
      HeaderIcon={Building2}
      columns={columns}
      apiEndpoint='usuarios/admins-institucion'
      FormComponent={FormComponent}
      gridTemplateColumns='1.5fr 1.5fr 1fr 1fr 1fr 0.8fr'
      singularEntityName='Admin Institucional'
    />
  )
}

export default InstitutionAdminsManagement
